import { css } from '@emotion/react';
import { createContext, ReactNode, useContext } from 'react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { useEverythingYouNeedForADropdown } from './LabeledInputs/LabeledInputLib';

const DropdownCloseContext = createContext<(() => void) | null>(null);

const DROPDOWN_BUTTON_STYLE = css`
  label: DROPDOWN_BUTTON_STYLE;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 40px;
  height: 40px;
  background: ${COLORS2.alwaysDark.background.dark};
  border: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${COLORS2.alwaysDark.text.white};

  &:hover {
    background: ${COLORS2.alwaysDark.background.black};
    border-color: ${COLORS2.alwaysDark.text.lightGray};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DROPDOWN_MENU_STYLE = css`
  label: DROPDOWN_MENU_STYLE;
  position: fixed;
  background: ${COLORS2.alwaysDark.background.black};
  border: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  min-width: 220px;
  z-index: 99999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const DROPDOWN_MENU_ITEM_STYLE = css`
  label: DROPDOWN_MENU_ITEM_STYLE;
  padding: ${LAYOUT2.spacing2}px ${LAYOUT2.spacing3}px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  ${TYPOGRAPHY2.xSmall};
  color: ${COLORS2.alwaysDark.text.white};
  font-size: ${pxToRem(14)};
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${COLORS2.alwaysDark.background.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const DROPDOWN_MENU_ITEM_SHIMMER_STYLE = css`
  label: DROPDOWN_MENU_ITEM_SHIMMER_STYLE;

  @keyframes dropdownMenuItemTextShimmer {
    0%   { background-position: -150% 0; }
    100% { background-position: 250% 0; }
  }

  /* Text-only shimmer. The menu item's background is left alone; instead the
     text fill is replaced with a clipped background: a dim base color
     (rgba .7 — ~30% darker than enabled white) plus a narrow animated bright
     slice that slides across, brightening the glyphs it passes over to full
     white. background-clip: text + transparent fill masks both layers to the
     glyph shape; alpha compositing of the slice over the base produces the
     brighten-on-pass effect — no blend mode needed.
     Caveat: SVG children using fill="currentColor" will also render
     transparent under this style. Only used with text-only items today. */
  background-color: rgba(255, 255, 255, 0.7);
  background-image: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 1) 50%,
    transparent 70%
  );
  background-size: 60% 100%;
  background-repeat: no-repeat;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: dropdownMenuItemTextShimmer 1.4s ease-in-out infinite;
`;

export const DropdownItem = ({
  onClick,
  disabled,
  shimmer,
  children,
}: {
  onClick?: () => void;
  disabled?: boolean;
  shimmer?: boolean;
  children: ReactNode;
}) => {
  const close = useContext(DropdownCloseContext);
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    close?.();
  };
  return (
    <div
      role='menuitem'
      css={[DROPDOWN_MENU_ITEM_STYLE, shimmer && DROPDOWN_MENU_ITEM_SHIMMER_STYLE]}
      onClick={handleClick}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !shimmer ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {children}
    </div>
  );
};

const DropdownRoot = ({
  triggerContent,
  children,
  onClose,
}: {
  triggerContent: ReactNode;
  children: ReactNode;
  onClose?: () => void;
}) => {
  const {
    dropdown,
    containerRef,
    dropdownRef,
    inputAbsoluteRect,
    isPicklistOpen,
    setIsPicklistOpen,
  } = useEverythingYouNeedForADropdown();

  const handleToggle = () => setIsPicklistOpen(!isPicklistOpen);
  const close = () => {
    setIsPicklistOpen(false);
    onClose?.();
  };

  return (
    <div ref={containerRef} css={css`label: DROPDOWN_CONTAINER; position: relative; display: flex; align-items: center;`}>
      <div css={DROPDOWN_BUTTON_STYLE} onClick={handleToggle} role='button' tabIndex={0} onKeyDown={event => event.key === 'Enter' && handleToggle()}>
        {triggerContent}
      </div>
      {dropdown(() => (
        <DropdownCloseContext.Provider value={close}>
          <div
            ref={dropdownRef}
            css={DROPDOWN_MENU_STYLE}
            style={{
              position: 'fixed',
              top: `${inputAbsoluteRect.top + inputAbsoluteRect.height + 4}px`,
              left: `${inputAbsoluteRect.left}px`,
            }}
            role='menu'
          >
            {children}
          </div>
        </DropdownCloseContext.Provider>
      ))}
    </div>
  );
};

export const Dropdown = Object.assign(DropdownRoot, { Item: DropdownItem });
